import { useEffect, useState } from 'react';
import { Card, Field, inputClass } from '@/components/Card';
import { Chip, ChipRow } from '@/components/ChipRow';
import { api } from '@/lib/api';
import type { Program, WalletItem } from '@shared/types';

export function WalletPage() {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [wallet, setWallet] = useState<WalletItem[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [tier, setTier] = useState('gold');
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    const data = await api.wallet();
    setWallet(data.wallet);
    setPrograms(data.programs);
  }

  useEffect(() => {
    reload().catch((err) => setError(err instanceof Error ? err.message : 'Wallet failed to load.'));
  }, []);

  const enrolled = (id: string) => wallet.find((item) => item.programId === id && item.enrolled);

  async function toggle(program: Program) {
    const current = enrolled(program.id);
    if (current) {
      await api.deleteWallet(current.id);
    } else {
      await api.saveWallet({
        programId: program.id,
        enrolled: true,
        label: program.name,
        tier: program.id === 'united_status' ? tier : null,
      });
    }
    await reload();
  }

  return (
    <div className="space-y-4">
      <Card>
        <h2 className="font-display text-[30px] leading-none text-navy">Benefit Wallet</h2>
        <p className="mt-2 text-sm text-muted">
          Manual cards, memberships, and status. CLEAR Plus and CLEAR Concierge are separate on purpose. Nothing here is synced from Amex, United, or CLEAR.
        </p>
        <ChipRow className="mt-3">
          {wallet.filter((w) => w.enrolled).map((item) => (
            <Chip key={item.id} tone="navy">
              {item.label || item.programId}
            </Chip>
          ))}
          {wallet.filter((w) => w.enrolled).length === 0 && <Chip tone="line">Empty wallet</Chip>}
        </ChipRow>
        <button
          className="mt-4 w-full rounded-card border border-line px-4 py-2.5 text-sm font-semibold"
          onClick={async () => {
            const data = await api.demoWallet();
            setWallet(data.wallet);
            setNotice(data.notice);
            await reload();
          }}
        >
          Load demo wallet
        </button>
        {notice && <p className="mt-2 text-[12px] text-muted">{notice}</p>}
      </Card>

      <Card>
        <Field label="United status tier (if enrolled)">
          <select className={inputClass} value={tier} onChange={(e) => setTier(e.target.value)}>
            <option value="silver">Silver</option>
            <option value="gold">Gold</option>
            <option value="platinum">Platinum</option>
            <option value="1k">1K</option>
            <option value="global_services">Global Services</option>
          </select>
        </Field>
      </Card>

      {programs.map((program) => {
        const on = Boolean(enrolled(program.id));
        return (
          <Card key={program.id}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <ChipRow>
                  <Chip tone={on ? 'success' : 'line'}>{on ? 'In wallet' : 'Not enrolled'}</Chip>
                  <Chip>{program.kind}</Chip>
                </ChipRow>
                <h3 className="mt-2 font-display text-2xl text-navy">{program.name}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted">{program.summary}</p>
              </div>
            </div>
            <button
              className={`mt-3 w-full rounded-card px-4 py-2.5 text-sm font-semibold ${
                on ? 'border border-line' : 'bg-navy text-sand'
              }`}
              onClick={() => toggle(program)}
            >
              {on ? 'Remove' : 'Add to wallet'}
            </button>
          </Card>
        );
      })}
      {error && <p className="text-sm text-alert">{error}</p>}
    </div>
  );
}
